/**
 * Material-UI to Crayon UI theme adapter.
 * Adapts Material-UI theme for use with C1Chat component.
 * 
 * This adapter converts the existing Material-UI theme to the Crayon UI theme
 * format required by the Thesys C1Chat component.
 */

import type { Theme as MuiTheme } from "@mui/material/styles";
// Import Theme and ThemeMode types from Crayon UI
// Using direct path to avoid module resolution issues
import type { Theme, ThemeMode } from "@crayonai/react-ui/dist/components/ThemeProvider/types";

/**
 * Adapts Material-UI theme to Crayon UI theme for C1Chat component.
 * 
 * @param muiTheme - The Material-UI theme object
 * @returns Crayon UI theme configuration with light and dark themes
 */
export function adaptMuiThemeToCrayon(muiTheme: MuiTheme): {
  theme: Theme;
  darkTheme: Theme;
  mode: ThemeMode;
} {
  const isDark = muiTheme.palette.mode === "dark";
  
  // Extract colors from Material-UI theme
  const primaryColor = muiTheme.palette.primary.main;
  const secondaryColor = muiTheme.palette.secondary?.main || primaryColor;
  const background = muiTheme.palette.background.default;
  const paper = muiTheme.palette.background.paper;
  const textPrimary = muiTheme.palette.text.primary;
  const textSecondary = muiTheme.palette.text.secondary;
  const errorColor = muiTheme.palette.error.main;
  const successColor = muiTheme.palette.success?.main || "#27AE60";
  
  // Extract font family from typography
  const fontFamily = muiTheme.typography.fontFamily || "proximanova, arial";
  
  // Create base Crayon theme based on template structure
  // Using template theme as reference: template-c1-next/src/theme.ts
  const baseTheme: Theme = {
    // Fonts - using Proxima Nova from existing theme
    fontBody: `375 16px/1.5 ${fontFamily}`,
    fontBodyLetterSpacing: "0px",
    fontBodyLink: `375 16px/1.5 ${fontFamily}`,
    fontBodyLinkLetterSpacing: "0px",
    fontBodyHeavy: `450 16px/1.5 ${fontFamily}`,
    fontBodyHeavyLetterSpacing: "0px",
    fontBodyMedium: `375 16px/1.5 ${fontFamily}`,
    fontBodyMediumLetterSpacing: "0px",
    fontBodySmallHeavy: `450 14px/1.5 ${fontFamily}`,
    fontBodySmallHeavyLetterSpacing: "0px",
    fontBodySmall: `375 14px/1.5 ${fontFamily}`,
    fontBodySmallLetterSpacing: "0px",
    fontBodyLarge: `375 18px/1.5 ${fontFamily}`,
    fontBodyLargeLetterSpacing: "0px",
    fontBodyLargeHeavy: `450 18px/1.5 ${fontFamily}`,
    fontBodyLargeHeavyLetterSpacing: "0px",
    
    // Label fonts
    fontLabel: `375 16px/1.2 ${fontFamily}`,
    fontLabelLetterSpacing: "0px",
    fontLabelHeavy: `450 16px/1.2 ${fontFamily}`,
    fontLabelHeavyLetterSpacing: "0px",
    fontLabelSmall: `375 14px/1.2 ${fontFamily}`,
    fontLabelSmallLetterSpacing: "0px",
    fontLabelSmallHeavy: `450 14px/1.2 ${fontFamily}`,
    fontLabelSmallHeavyLetterSpacing: "0px",
    fontLabelExtraSmall: `375 12px/1.2 ${fontFamily}`,
    fontLabelExtraSmallLetterSpacing: "0px",
    fontLabelExtraSmallHeavy: `450 12px/1.2 ${fontFamily}`,
    fontLabelExtraSmallHeavyLetterSpacing: "0px",
    fontLabelLarge: `375 18px/1.2 ${fontFamily}`,
    fontLabelLargeLetterSpacing: "0px",
    fontLabelLargeHeavy: `450 18px/1.2 ${fontFamily}`,
    fontLabelLargeHeavyLetterSpacing: "0px",
    fontLabelMediumHeavy: `450 16px/1.2 ${fontFamily}`,
    fontLabelMediumHeavyLetterSpacing: "0px",
    fontLabelMedium: `375 16px/1.2 ${fontFamily}`,
    fontLabelMediumLetterSpacing: "0px",
    
    // Heading fonts
    fontHeadingLarge: `550 28px/1.15 ${fontFamily}`,
    fontHeadingLargeLetterSpacing: "-0.25px",
    fontHeadingMedium: `550 24px/1.15 ${fontFamily}`,
    fontHeadingMediumLetterSpacing: "-0.25px",
    fontHeadingSmall: `550 18px/1.25 ${fontFamily}`,
    fontHeadingSmallLetterSpacing: "0px",
    fontHeadingExtraSmall: `550 16px/1.25 ${fontFamily}`,
    fontHeadingExtraSmallLetterSpacing: "0px",
    
    // Label2 fonts
    fontLabel2ExtraSmall: `375 12px/1.2 ${fontFamily}`,
    fontLabel2ExtraSmallLetterSpacing: "0px",
    fontLabel2ExtraSmallHeavy: `450 12px/1.2 ${fontFamily}`,
    fontLabel2ExtraSmallHeavyLetterSpacing: "0px",
    
    // Number fonts
    fontNumberLarge: `375 18px/1.5 ${fontFamily}`,
    fontNumberLargeLetterSpacing: "0px",
    fontNumberLargeHeavy: `450 18px/1.5 ${fontFamily}`,
    fontNumberLargeHeavyLetterSpacing: "0px",
    fontNumberHeavy: `450 16px/1.5 ${fontFamily}`,
    fontNumberHeavyLetterSpacing: "0px",
    fontNumber: `375 16px/1.5 ${fontFamily}`,
    fontNumberLetterSpacing: "0px",
    fontNumberSmall: `375 14px/1.5 ${fontFamily}`,
    fontNumberSmallLetterSpacing: "0px",
    fontNumberSmallHeavy: `450 14px/1.5 ${fontFamily}`,
    fontNumberSmallHeavyLetterSpacing: "0px",
    fontNumberExtraSmall: `375 12px/1.5 ${fontFamily}`,
    fontNumberExtraSmallLetterSpacing: "0px",
    fontNumberExtraSmallHeavy: `450 12px/1.5 ${fontFamily}`,
    fontNumberExtraSmallHeavyLetterSpacing: "0px",
    fontNumberTitle: `550 28px/1.5 ${fontFamily}`,
    fontNumberTitleLetterSpacing: "0px",
    fontNumberTitleMedium: `550 24px/1.5 ${fontFamily}`,
    fontNumberTitleMediumLetterSpacing: "0px",
    
    // Background fills
    backgroundFills: background,
    containerFills: paper,
    overlayFills: "rgba(0, 0, 0, 0.5)",
    sunkFills: "rgba(0, 0, 0, 0.04)",
    containerHoverFills: "rgba(0, 0, 0, 0.04)",
    dangerFills: "#fff0f0",
    successFills: "#ebffe7",
    infoFills: "#f0f3ff",
    elevatedFills: "rgba(255, 255, 255, 0.2)",
    alertFills: "#fff8f4",
    sunkBgFills: "rgba(0, 0, 0, 0.04)",
    invertedFills: "#101010",
    
    // Strokes
    strokeDefault: "rgba(0, 0, 0, 0.06)",
    strokeInteractiveEl: "rgba(0, 0, 0, 0.1)",
    strokeInteractiveElHover: "rgba(0, 0, 0, 0.3)",
    strokeInteractiveElSelected: "rgba(0, 0, 0, 0.5)",
    strokeEmphasis: "rgba(0, 0, 0, 0.2)",
    strokeAccent: "rgba(255, 255, 255, 0.04)",
    strokeAccentEmphasis: "rgba(255, 255, 255, 0.08)",
    strokeInfo: "#e7eeff",
    strokeInfoEmphasis: "#0084f4",
    strokeAlert: "#fff1e4",
    strokeAlertEmphasis: "#efa51b",
    strokeSuccess: "#d3ffd1",
    strokeSuccessEmphasis: successColor,
    strokeDanger: "#ffe9e9",
    strokeDangerEmphasis: errorColor,
    
    // Text colors
    brandText: "rgba(255, 255, 255, 1)",
    brandSecondaryText: "rgba(255, 255, 255, 0.7)",
    primaryText: textPrimary,
    secondaryText: textSecondary,
    disabledText: "#afafaf",
    dangerText: "#a8123b",
    successText: "#067b31",
    linkText: "#81450a",
    infoText: "#0055a0",
    accentPrimaryText: "rgba(255, 255, 255, 1)",
    accentSecondaryText: "rgba(255, 255, 255, 0.7)",
    accentDisabledText: "rgba(255, 255, 255, 0.4)",
    successPrimaryText: "rgba(40, 140, 80, 1)",
    successInvertedText: "rgba(200, 255, 220, 1)",
    alertPrimaryText: "rgba(180, 130, 0, 1)",
    alertInvertedText: "rgba(255, 240, 180, 1)",
    dangerPrimaryText: "rgba(180, 40, 50, 1)",
    dangerSecondaryText: "rgba(200, 60, 70, 1)",
    dangerDisabledText: "rgba(220, 160, 165, 1)",
    dangerInvertedPrimaryText: "rgba(255, 255, 255, 1)",
    dangerInvertedSecondaryText: "rgba(255, 255, 255, 0.7)",
    dangerInvertedDisabledText: "rgba(255, 255, 255, 0.4)",
    infoPrimaryText: "rgba(60, 100, 160, 1)",
    infoInvertedText: "rgba(200, 225, 255, 1)",
    
    // Interactive colors
    interactiveDefault: "rgba(255, 255, 255, 0.02)",
    interactiveHover: "rgba(0, 0, 0, 0.04)",
    interactivePressed: "rgba(0, 0, 0, 0.06)",
    interactiveDisabled: "rgba(255, 255, 255, 0.02)",
    interactiveAccent: primaryColor,
    interactiveAccentHover: primaryColor, // Can be adjusted for hover state
    interactiveAccentPressed: primaryColor, // Can be adjusted for pressed state
    interactiveAccentDisabled: `${primaryColor}33`, // 20% opacity
    interactiveDestructive: "#fff0f0",
    interactiveDestructiveHover: "#ffe9e9",
    interactiveDestructivePressed: "#ffdddf",
    interactiveDestructiveDisabled: "#fff0f0",
    
    // Highlight
    highlightSubtle: "rgba(0, 0, 0, 0.02)",
    
    // Chat-specific colors
    chatContainerBg: background,
    chatAssistantResponseBg: paper,
    chatAssistantResponseText: textPrimary,
    chatUserResponseBg: "#d6d6d6",
    chatUserResponseText: "#242424",
    
    // Chart palette (using Caravel orange as primary)
    defaultChartPalette: [
      "#fce5d2",
      "#f9d5b6",
      "#f7c59b",
      "#f5b680",
      primaryColor,
      "#cd803e",
      "#a96933",
      "#855328",
      "#603c1d"
    ],
    
    // Spacing
    spacing0: "0px",
    spacing3xs: "2px",
    spacing2xs: "4px",
    spacingXs: "6px",
    spacingS: "8px",
    spacingM: "12px",
    spacingL: "18px",
    spacingXl: "24px",
    spacing2xl: "36px",
    spacing3xl: "48px",
    
    // Border radius
    rounded0: "0px",
    rounded3xs: "2px",
    rounded2xs: "4px",
    roundedXs: "6px",
    roundedS: "8px",
    roundedM: "10px",
    roundedL: "12px",
    roundedXl: "16px",
    rounded2xl: "20px",
    rounded3xl: "24px",
    rounded4xl: "28px",
    roundedFull: "999px",
    roundedClickable: "10px",
    
    // Shadows
    shadowS: "0px 1px 2px rgba(0, 0, 0, 0.04)",
    shadowM: "0px 4px 6px rgba(0, 0, 0, 0.04)",
    shadowL: "0px 1px 8px rgba(0, 0, 0, 0.08)",
    shadowXl: "0px 10px 15px rgba(0, 0, 0, 0.1)",
    
    // Theme metadata
    mode: "Light",
    name: "Caravel",
    typography: "Proxima Nova",
  } as Theme;
  
  // Dark theme - adjust colors for dark mode
  const darkTheme: Theme = {
    ...baseTheme,
    backgroundFills: "#1b1b1b",
    containerFills: "#2e2e2e",
    overlayFills: "rgba(0, 0, 0, 0.6)",
    sunkFills: "rgba(255, 255, 255, 0.04)",
    containerHoverFills: "rgba(0, 0, 0, 0.04)",
    dangerFills: "#4f0016",
    successFills: "#003912",
    infoFills: "#00244a",
    elevatedFills: "rgba(255, 255, 255, 0.04)",
    alertFills: "#442b00",
    sunkBgFills: "rgba(0, 0, 0, 0.04)",
    invertedFills: "#f1f1f1",
    
    strokeDefault: "rgba(255, 255, 255, 0.06)",
    strokeInteractiveEl: "rgba(255, 255, 255, 0.1)",
    strokeInteractiveElHover: "rgba(255, 255, 255, 0.2)",
    strokeInteractiveElSelected: "rgba(255, 255, 255, 0.5)",
    strokeEmphasis: "rgba(255, 255, 255, 0.2)",
    strokeAccent: "rgba(0, 0, 0, 0.04)",
    strokeAccentEmphasis: "rgba(0, 0, 0, 0.08)",
    strokeInfo: "#00244a",
    strokeInfoEmphasis: "#0084f4",
    strokeAlert: "#442b00",
    strokeAlertEmphasis: "#efa51b",
    strokeSuccess: "#003912",
    strokeSuccessEmphasis: successColor,
    strokeDanger: "#4f0016",
    strokeDangerEmphasis: errorColor,
    
    primaryText: "#f1f1f1",
    secondaryText: "#898989",
    disabledText: "#5a5a5a",
    dangerText: "#ff95a1",
    successText: "#86d98e",
    linkText: "#f2bb93",
    infoText: "#84b5ff",
    successPrimaryText: "rgba(120, 240, 180, 1)",
    successInvertedText: "rgba(20, 60, 40, 1)",
    alertPrimaryText: "rgba(243, 207, 80, 1)",
    alertInvertedText: "rgba(187, 122, 0, 1)",
    dangerPrimaryText: "rgba(232, 73, 83, 1)",
    dangerSecondaryText: "rgba(255, 140, 150, 1)",
    dangerDisabledText: "rgba(120, 60, 65, 1)",
    dangerInvertedPrimaryText: "rgba(255, 255, 255, 1)",
    dangerInvertedSecondaryText: "rgba(255, 255, 255, 0.5)",
    dangerInvertedDisabledText: "rgba(255, 255, 255, 0.25)",
    infoPrimaryText: "rgb(94, 177, 255)",
    infoInvertedText: "rgba(10, 25, 40, 1)",
    
    interactiveDefault: "rgba(255, 255, 255, 0)",
    interactiveHover: "rgba(255, 255, 255, 0.04)",
    interactivePressed: "rgba(255, 255, 255, 0.08)",
    interactiveDisabled: "rgba(255, 255, 255, 0)",
    interactiveAccentDisabled: "rgba(241, 150, 73, 0.2)",
    
    highlightSubtle: "rgba(255, 255, 255, 0.02)",
    
    chatContainerBg: "#1b1b1b",
    chatAssistantResponseBg: "#2e2e2e",
    chatAssistantResponseText: "#e4e4e4",
    chatUserResponseBg: "#2e2e2e",
    chatUserResponseText: "#e4e4e4",
    
    mode: "Dark",
  } as Theme;
  
  return {
    theme: baseTheme,
    darkTheme: darkTheme,
    mode: isDark ? "dark" : "light",
  };
}
